// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { formatISODate } from "@clipboard-health/date-time";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES } from "@src/appV2/api/constants";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { isDeviceLocationAlwaysAllowPermissionGranted } from "@src/appV2/Location/deviceLocation/permissions";
import { isOsWithoutAlwaysAllowOption } from "@src/appV2/Location/utils";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import {
  INTERVIEW_TYPE,
  interviewsApiUrl,
  InterviewStatus,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { type QueryObserverResult, useQueryClient } from "@tanstack/react-query";
import { differenceInHours, isAfter, parseISO, startOfDay } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { zonedTimeToUtc } from "date-fns-tz";
import { useEffect } from "react";

import { useCreateInterview } from "../../../../api/useCreateInterview";
import { PLACEMENT_TYPE } from "../../../../api/useCreatePlacementApplication";
import { type GetAvailabilityResponse } from "../../../../api/useGetInterviewAvailability";
import { type PlacementDetail } from "../../../../api/useGetPlacement";
import { type PlacementResponse } from "../../../../types/fetchPlacements.schema";
import { PLACEMENT_ACTION_TYPE, type PlacementActionType } from "../../utils/getPrimaryAction";
import { type PlacementDerivedData } from "../usePlacementPrimaryAction";
import { type usePlacementState } from "../usePlacementState";
import { type ApplicationOperationsReturn } from "./useApplicationOperations";

interface UseInterviewOperationsParams {
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  modals: ReturnType<typeof usePlacementState>["modals"];
  interviewState: ReturnType<typeof usePlacementState>["interviewState"];
  locationState: ReturnType<typeof usePlacementState>["locationState"];
  derivedData: PlacementDerivedData;
  applicationOperations: ApplicationOperationsReturn;
  refetchPlacement: () => Promise<QueryObserverResult<PlacementResponse>>;
  refetchInterviewAvailability: () => Promise<QueryObserverResult<GetAvailabilityResponse>>;
  refetchPlacementCandidate: () => Promise<QueryObserverResult<PlacementCandidate>>;
}

/**
 * Gets the current scheduled interview (BOOKED or PENDING) for reschedule operations.
 * Prioritizes in-person interviews over phone screen interviews.
 * Includes PENDING status interviews from the submit documents later flow.
 */
function getCurrentScheduledInterview(interviews: PlacementDetail["interviews"]) {
  const scheduledInterviews = interviews.filter(
    (interview) =>
      interview.attributes.status === InterviewStatus.BOOKED ||
      interview.attributes.status === InterviewStatus.PENDING
  );
  const phoneScreenInterview = scheduledInterviews.find(
    (interview) => interview.attributes.type === INTERVIEW_TYPE.PHONE_SCREEN
  );
  const inPersonInterview = scheduledInterviews.find(
    (interview) => interview.attributes.type === INTERVIEW_TYPE.IN_PERSON
  );
  return inPersonInterview ?? phoneScreenInterview ?? scheduledInterviews[0];
}

/**
 * Handles all interview-related operations (book, reschedule, location permissions).
 */
export function useInterviewOperations(params: UseInterviewOperationsParams) {
  const {
    placementDetails,
    modals,
    interviewState,
    locationState,
    derivedData,
    applicationOperations,
    refetchPlacement,
    refetchInterviewAvailability,
    refetchPlacementCandidate,
  } = params;

  const errorMessages = useCbhFlag(CbhFeatureFlag.HCP_APP_ERROR_MESSAGES, { defaultValue: {} });
  const { showErrorToast } = useToast();
  const { mutateAsync: createInterview, isLoading: isCreatingInterview } = useCreateInterview();
  const queryClient = useQueryClient();

  const { workerTimezone, hasApplied, isSignOnBonusPaid, incentiveProgram, eventContext } =
    derivedData;
  const {
    selectedInterviewTime,
    setSelectedInterviewTime,
    bookInterviewModalType,
    setBookInterviewModalType,
    isRescheduleForToday,
    setIsRescheduleForToday,
  } = interviewState;
  const { applicationPreferences } = applicationOperations;
  const {
    isOsWithoutAlwaysAllow,
    setIsOsWithoutAlwaysAllow,
    shouldShowLocationPermissionDialog,
    setShouldShowLocationPermissionDialog,
  } = locationState;

  // Show location permission dialog after interview booked dialog closes
  useEffect(() => {
    if (shouldShowLocationPermissionDialog && !modals.interviewBooked.modalIsOpen) {
      modals.locationPermission.openModal();
      setShouldShowLocationPermissionDialog(false);
    }
  }, [
    shouldShowLocationPermissionDialog,
    setShouldShowLocationPermissionDialog,
    modals.interviewBooked.modalIsOpen,
    modals.locationPermission,
  ]);

  async function checkLocationPermissionsAfterInterviewBooking() {
    if (!isCapacitorPlatform()) {
      return;
    }

    try {
      const hasAlwaysPermission = await isDeviceLocationAlwaysAllowPermissionGranted();
      const osWithoutAlwaysAllow = await isOsWithoutAlwaysAllowOption();
      setIsOsWithoutAlwaysAllow(osWithoutAlwaysAllow);

      if (!hasAlwaysPermission) {
        setShouldShowLocationPermissionDialog(true);
        logEvent(APP_V2_APP_EVENTS.INTERVIEW_LOCATION_PERMISSION_BOTTOM_SHEET_SHOWN_AFTER_BOOKING, {
          ...eventContext,
        });
      }
    } catch {
      // If we can't check the permission status, we'll assume it's not granted and show the dialog
      setShouldShowLocationPermissionDialog(true);
      logEvent(APP_V2_APP_EVENTS.INTERVIEW_LOCATION_PERMISSION_BOTTOM_SHEET_SHOWN_AFTER_BOOKING, {
        ...eventContext,
      });
    }
  }

  async function checkLocationPermissionsBeforeInterviewBooking(): Promise<boolean> {
    if (!isCapacitorPlatform()) {
      return true;
    }

    try {
      const hasAlwaysPermission = await isDeviceLocationAlwaysAllowPermissionGranted();
      const osWithoutAlwaysAllow = await isOsWithoutAlwaysAllowOption();
      setIsOsWithoutAlwaysAllow(osWithoutAlwaysAllow);

      if (!hasAlwaysPermission) {
        modals.interviewLocationPermission.openModal();
        logEvent(
          APP_V2_APP_EVENTS.INTERVIEW_LOCATION_PERMISSION_BOTTOM_SHEET_SHOWN_BEFORE_BOOKING,
          {
            ...eventContext,
            placementActionType: bookInterviewModalType,
          }
        );
        return false;
      }

      return true;
    } catch {
      // If we can't check the permission status, we'll assume it's not granted and show the dialog
      modals.interviewLocationPermission.openModal();
      logEvent(APP_V2_APP_EVENTS.INTERVIEW_LOCATION_PERMISSION_BOTTOM_SHEET_SHOWN_BEFORE_BOOKING, {
        ...eventContext,
        placementActionType: bookInterviewModalType,
      });
      return false;
    }
  }

  function handleInterviewError(error: unknown, defaultMessage: string): void {
    if (!isCbhApiAxiosError(error)) {
      showErrorToast(defaultMessage);
      return;
    }

    const errorResponse = error.response.data.errors.find((error) => error.code);
    if (errorResponse && errorMessages[errorResponse.code]) {
      showErrorToast(errorMessages[errorResponse.code]);
      return;
    }

    // Fallback to showing API error details
    const errorDetails = error.response.data.errors
      .map((error) => error.detail)
      .filter(Boolean)
      .join(", ");
    if (errorDetails) {
      showErrorToast(
        <Text variant="body2" color="inherit">
          {errorDetails}
        </Text>
      );
      return;
    }

    showErrorToast(defaultMessage);
  }

  async function handleBookInterview(
    time: { start: Date; end: Date },
    options?: { status?: InterviewStatus.PENDING | InterviewStatus.BOOKED }
  ): Promise<boolean> {
    try {
      const zonedTimeStart = zonedTimeToUtc(time.start, workerTimezone);
      const zonedTimeEnd = zonedTimeToUtc(time.end, workerTimezone);

      if (hasApplied) {
        const placementApplication = placementDetails?.applications?.at(-1);

        if (!placementApplication) {
          throw new Error("No placement application found");
        }

        const createInterviewPayload = {
          relatedResource: {
            type: API_TYPES.placementApplication,
            id: placementApplication.id,
          },
          start: zonedTimeStart,
          end: zonedTimeEnd,
          ...(isDefined(options?.status) && { status: options.status }),
        };

        await createInterview(createInterviewPayload);
        return true;
      }

      const baseApplyPayload = {
        placementType: PLACEMENT_TYPE.verified,
        skipErrorHandling: true,
        ...(isDefined(incentiveProgram) && !isSignOnBonusPaid ? { incentiveProgram } : {}),
        ...(isDefined(applicationPreferences) && {
          workerApplicationPreferenceDetails: applicationPreferences,
        }),
      };

      // For PENDING interviews (submit documents later flow), we need to:
      // 1. Create the placement application WITHOUT scheduleInterview
      // 2. Then create the interview with status: "Pending" using the application ID
      // This is because the backend only bypasses document validation when creating
      // the interview via POST /api/interviews with status: "Pending"
      if (options?.status === InterviewStatus.PENDING) {
        const applicationResponse = await applicationOperations.handleApply({
          ...baseApplyPayload,
          skipSuccessModal: true,
          pendingInterview: true,
        });

        try {
          await createInterview({
            relatedResource: {
              type: API_TYPES.placementApplication,
              id: applicationResponse.data.id,
            },
            start: zonedTimeStart,
            end: zonedTimeEnd,
            status: InterviewStatus.PENDING,
          });
        } catch (interviewError) {
          // Log the orphaned application for manual reconciliation
          // Note: There is no API to delete/withdraw applications, so we can only log
          logEvent(APP_V2_APP_EVENTS.INTERVIEW_CREATION_FAILURE, {
            ...eventContext,
            orphanedApplicationId: applicationResponse.data.id,
            pendingInterview: true,
            error:
              interviewError instanceof Error ? interviewError.message : String(interviewError),
          });
          throw interviewError;
        }

        return true;
      }

      // Standard flow: create application with scheduleInterview in one call
      await applicationOperations.handleApply({
        ...baseApplyPayload,
        scheduleInterview: {
          start: zonedTimeStart.toISOString(),
          end: zonedTimeEnd.toISOString(),
        },
      });
      return true;
    } catch (error) {
      handleInterviewError(error, "Failed to book interview.");
      return false;
    }
  }

  async function handleRescheduleInterview(time: { start: Date; end: Date }): Promise<boolean> {
    try {
      const zonedTimeStart = zonedTimeToUtc(time.start, workerTimezone);
      const zonedTimeEnd = zonedTimeToUtc(time.end, workerTimezone);

      const rescheduledInterview = getCurrentScheduledInterview(placementDetails.interviews);

      if (!rescheduledInterview) {
        throw new Error(
          "Could not find an interview to reschedule based on the status and start date"
        );
      }

      // Preserve the PENDING status when rescheduling a pending interview (from submit documents later flow)
      const shouldPreservePendingStatus =
        rescheduledInterview.attributes.status === InterviewStatus.PENDING;

      await createInterview({
        relatedResource: {
          type: API_TYPES.interview,
          id: rescheduledInterview.id,
        },
        start: zonedTimeStart,
        end: zonedTimeEnd,
        ...(isDefined(rescheduledInterview.attributes.type) && {
          interviewType: rescheduledInterview.attributes.type,
        }),
        ...(shouldPreservePendingStatus && {
          status: InterviewStatus.PENDING,
        }),
      });

      return true;
    } catch (error) {
      handleInterviewError(error, "Failed to reschedule interview.");
      return false;
    }
  }

  async function handleBookOrRescheduleInterview(
    time: { start: Date; end: Date },
    options?: { status?: InterviewStatus.PENDING | InterviewStatus.BOOKED }
  ) {
    const success = await (bookInterviewModalType === PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW
      ? handleBookInterview(time, options)
      : handleRescheduleInterview(time));
    await Promise.all([
      refetchPlacement(),
      refetchInterviewAvailability(),
      queryClient.invalidateQueries({ queryKey: [interviewsApiUrl] }),
    ]);

    await refetchPlacementCandidate();

    if (success) {
      modals.interviewBooked.openModal();
      await checkLocationPermissionsAfterInterviewBooking();
    }

    return success;
  }

  function isRescheduleToFutureDay(newTime: { start: Date; end: Date }): boolean {
    const currentInterview = getCurrentScheduledInterview(placementDetails.interviews);

    if (!currentInterview) {
      return false;
    }

    const currentDate = startOfDay(parseISO(currentInterview.attributes.start));
    const newDate = startOfDay(newTime.start);

    // add 8 hour difference check in case of timezone weirdness
    return isAfter(newDate, currentDate) && differenceInHours(newDate, currentDate) > 8;
  }

  async function openBookInterviewModal(
    type: PlacementActionType,
    interviewAvailability: Array<{ start: Date }>,
    isRescheduleToToday = false
  ) {
    setBookInterviewModalType(type);
    setIsRescheduleForToday(isRescheduleToToday);

    // Check location permissions before allowing interview booking
    const hasLocationPermissions = await checkLocationPermissionsBeforeInterviewBooking();
    if (!hasLocationPermissions) {
      return;
    }

    // Check if this is a phone screen and show phone confirmation first
    const isPhoneScreen =
      placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType ===
      INTERVIEW_TYPE.PHONE_SCREEN;
    if (isPhoneScreen && type === PLACEMENT_ACTION_TYPE.BOOK_INTERVIEW) {
      modals.phoneConfirmation.openModal();
      return;
    }

    modals.bookInterview.openModal();
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_BOOKING_BOTTOMSHEET_OPENED, {
      ...eventContext,
      placementActionType: type,
      totalTimeSlots: interviewAvailability.length,
      totalAvailableDates: new Set(interviewAvailability.map((slot) => formatISODate(slot.start)))
        .size,
    });
  }

  function continueAfterPhoneConfirmation(interviewAvailability: Array<{ start: Date }>) {
    // Open the interview booking modal after phone confirmation
    modals.bookInterview.openModal();
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_BOOKING_BOTTOMSHEET_OPENED, {
      ...eventContext,
      placementActionType: bookInterviewModalType,
      totalTimeSlots: interviewAvailability.length,
      totalAvailableDates: new Set(interviewAvailability.map((slot) => formatISODate(slot.start)))
        .size,
    });
  }

  return {
    handleBookInterview,
    handleRescheduleInterview,
    handleBookOrRescheduleInterview,
    openBookInterviewModal,
    continueAfterPhoneConfirmation,
    checkLocationPermissionsAfterInterviewBooking,
    isCreatingInterview,
    selectedInterviewTime,
    setSelectedInterviewTime,
    bookInterviewModalType,
    isOsWithoutAlwaysAllow,
    isRescheduleToFutureDay,
    isRescheduleForToday,
  };
}

export type InterviewOperationsReturn = ReturnType<typeof useInterviewOperations>;
